import { useRef } from 'react';
import styles from './WhyBrand.module.css';
import shared from '../HomePage.module.css';
import useDecoParallax from '../hooks/useDecoParallax.js';

/**
 * ═══ WHY / BRAND ═══
 * 세 개의 원본 <section>을 하나의 컴포넌트로 묶었다:
 *  1) .why  (id="why")   — 빈틈 전환기 다이어그램(유아 영어 → 병목 → 초등 영어 → 브릿지)
 *  2) .values (id="values") — 교육의 선순환 4대 핵심 가치
 *  3) .why-features       — 핵심 강점 3카드(JOYFUL/STEADY/SMART)
 */
export default function WhyBrand() {
  const valuesDecoRef = useRef(null);
  const featuresDecoRef = useRef(null);

  useDecoParallax(valuesDecoRef);
  useDecoParallax(featuresDecoRef);

  return (
    <>
      {/* ═══ WHY / BRAND ═══ */}
      <section className={`${shared.section} ${styles.why}`} id="why">
        <div className="wrap">
          <div className={`${styles.bridgeHead} ${shared.reveal}`}>
            <span className={shared.eyebrow}>Why English POP</span>
            <h3>유아와 초등 사이, 영어의 빈틈 전환기</h3>
            <p>&quot;노출은 많았는데, 보여줄 게 없다&quot; 학부모님의 불안, 안심으로 바뀝니다.</p>
          </div>
          <div className={`${styles.gapFlow} ${shared.reveal}`}>
            <div className={styles.gapCard}>
              <h4>
                유아 영어 <span>(6~7세)</span>
              </h4>
              <ul>
                <li>100% 경험 중심의 노출</li>
                <li>환경적인 소음 수준의 영어</li>
              </ul>
            </div>
            <div className={styles.gapArrow}>
              <span className={styles.gapArrowGlyph}>➜</span>
              <span>병목 현상</span>
            </div>
            <div className={styles.gapCard}>
              <h4>
                초등 영어 <span>(8~9세)</span>
              </h4>
              <ul>
                <li>본격적인 영어 학습 시작</li>
                <li>체계적인 학습에 대한 모호성</li>
              </ul>
            </div>
          </div>
          <div className={`${styles.bridgeBox} ${shared.reveal}`}>
            <h4>놀이에서 학습으로, 완벽한 브릿지(Bridge)</h4>
            <p>
              기존 유아 영어를 대체하는 것이 아닌, 6~9세 아이들만을 위한 최초의 체계.
              <br />
              흩어진 경험을 규칙과 패턴을 가진 언어망으로 구조화합니다.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ 교육의 선순환 (4대 핵심 가치) ═══ */}
      <section className={`${shared.section} ${styles.values}`} id="values">
        <div ref={valuesDecoRef} className={shared.secDeco} aria-hidden="true">
          <span className={shared.decoBlob} style={{ '--c': '0,181,226', width: '190px', height: '190px', top: '-70px', right: '4%' }}></span>
          <span className={shared.decoBlob} style={{ '--c': '255,196,46', width: '74px', height: '74px', top: '22%', left: '8%' }}></span>
          <span className={shared.decoRing} style={{ '--c': '255,100,0', width: '100px', height: '100px', bottom: '12%', left: '5%' }}></span>
        </div>
        <div className="wrap">
          <div className={`${shared.secHead} ${shared.reveal}`}>
            <h2>믿음에서 시작해 성장으로 완성되는 교육의 선순환</h2>
            <p>잉글리시팝이 약속하는 4가지 핵심 가치</p>
          </div>
          <div className={styles.valuesGrid}>
            <div className={`${styles.valueCard} ${shared.reveal}`}>
              <div className={styles.valueIco} style={{ background: 'rgba(10,68,160,.1)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#0a44a0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6l7-3z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <span className="en">Trust</span>
              <h3>신뢰</h3>
              <p>교육 전문 기업 비상교육만의 검증된 시스템과 교과 연계 커리큘럼으로 확실한 품질 보증</p>
            </div>
            <div className={`${styles.valueCard} ${shared.reveal}`}>
              <div className={styles.valueIco} style={{ background: 'rgba(255,100,0,.12)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#ff6400" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="3 17 9 11 13 15 21 7" />
                  <polyline points="15 7 21 7 21 13" />
                </svg>
              </div>
              <span className="en">Growth</span>
              <h3>성장</h3>
              <p>단순 반복을 넘어 원리를 이해하고 아이의 실력이 팝(POP) 터지는 실제적 성장 경험</p>
            </div>
            <div className={`${styles.valueCard} ${shared.reveal}`}>
              <div className={styles.valueIco} style={{ background: 'rgba(0,181,226,.12)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#00b5e2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="9" r="6" />
                  <path d="M8.5 14l-1.5 7 5-3 5 3-1.5-7" />
                </svg>
              </div>
              <span className="en">Expert</span>
              <h3>전문성</h3>
              <p>유아 및 초등 저학년의 발달 특성을 완벽히 이해하는 특화 강사 양성 및 관리</p>
            </div>
            <div className={`${styles.valueCard} ${shared.reveal}`}>
              <div className={styles.valueIco} style={{ background: 'rgba(10,68,160,.1)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#0a44a0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9.5 13.5a4 4 0 0 0 5.66 0l2.5-2.5a4 4 0 0 0-5.66-5.66l-1.2 1.2" />
                  <path d="M14.5 10.5a4 4 0 0 0-5.66 0l-2.5 2.5a4 4 0 0 0 5.66 5.66l1.2-1.2" />
                </svg>
              </div>
              <span className="en">Alignment</span>
              <h3>연결</h3>
              <p>파편화된 유아 영어 경험을 체계화하여 초등 단계의 탄탄한 실력으로 브릿지 연결</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 핵심 강점 3카드 (JOYFUL / DAILY / SMART) ═══ */}
      <section className={`${shared.section} ${styles.whyFeatures}`}>
        <div ref={featuresDecoRef} className={shared.secDeco} aria-hidden="true">
          <span className={shared.decoRing} style={{ '--c': '0,181,226', width: '112px', height: '112px', top: '-40px', left: '6%' }}></span>
          <span className={shared.decoBlob} style={{ '--c': '255,100,0', width: '150px', height: '150px', bottom: '-50px', right: '5%' }}></span>
        </div>
        <div className="wrap">
          <div className={`${shared.secHead} ${shared.reveal}`}>
            <h2>전환기 영어의 첫번째 기준</h2>
            <p>6~9세는 언어 두뇌가 가장 활발하게 자라는 시기. 잉글리시팝은 재미로 시작해 습관으로 완성합니다.</p>
          </div>
          <div className={styles.whyGrid}>
            <div className={`${styles.whyCard} ${shared.reveal}`}>
              <div className={styles.whyIco} style={{ background: 'rgba(255,100,0,.12)' }}>
                <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
                  <path d="M16 3.5l2.6 7.4 7.4 2.6-7.4 2.6L16 23.5l-2.6-7.4L6 13.5l7.4-2.6L16 3.5Z" fill="#ff6400" />
                  <circle cx="25.5" cy="23" r="2.3" fill="#ffc62e" />
                  <circle cx="7.5" cy="24" r="1.5" fill="#ff8c3e" />
                </svg>
              </div>
              <span className={styles.tag}>JOYFUL START</span>
              <h3>놀이처럼 시작하는 영어</h3>
              <p>5명의 친구 캐릭터와 노래·게임·스토리로 영어를 처음부터 &apos;재미있는 것&apos;으로 각인시킵니다.</p>
            </div>
            <div className={`${styles.whyCard} ${shared.reveal}`}>
              <div className={styles.whyIco} style={{ background: 'rgba(0,181,226,.12)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#00b5e2" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="23 4 23 10 17 10" />
                  <polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
              </div>
              <span className={styles.tag}>STEADY HABIT</span>
              <h3>규칙적인 흔들림 없는 습관</h3>
              <p>꾸준하고 규칙적인 학습 루틴으로 영어를 &apos;특별한 공부&apos;가 아닌 &apos;성장하는 습관&apos;으로 만듭니다.</p>
            </div>
            <div className={`${styles.whyCard} ${shared.reveal}`}>
              <div className={styles.whyIco} style={{ background: 'rgba(10,68,160,.1)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#0a44a0" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
              </div>
              <span className={styles.tag}>SMART GROWTH</span>
              <h3>데이터로 성장하는 6단계</h3>
              <p>Pop부터 Link까지, 아이의 현재 수준을 진단하고 다음 단계로 정확히 이어주는 스마트 시스템.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
